import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import {
  HelpCircle, Plus, Pencil, Trash2, Eye, EyeOff,
  GripVertical, Save, X, RefreshCw, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';

const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    is_published: true
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/faq`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFaqs(data.faqs || []);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load FAQs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.question || !formData.answer) {
      toast({
        title: 'Error',
        description: 'Question and answer are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingFaq 
        ? `${API_BASE}/api/admin/faq/${editingFaq.id}`
        : `${API_BASE}/api/admin/faq`;
      
      const response = await fetch(url, {
        method: editingFaq ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: editingFaq ? 'FAQ updated' : 'FAQ created'
        });
        resetForm();
        fetchFaqs();
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save FAQ',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question || '',
      answer: faq.answer || '',
      category: faq.category || 'general',
      is_published: faq.is_published ?? true
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/faq/${faqId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: 'Deleted',
          description: 'FAQ deleted successfully'
        });
        fetchFaqs();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete FAQ',
        variant: 'destructive'
      });
    }
  };

  const togglePublished = async (faq) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/admin/faq/${faq.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_published: !faq.is_published })
      });
      fetchFaqs();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      category: 'general',
      is_published: true
    });
    setEditingFaq(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'trainers', label: 'For Trainers' },
    { value: 'users', label: 'For Users' },
    { value: 'payments', label: 'Payments' },
    { value: 'account', label: 'Account' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 dark:text-white">
            <HelpCircle className="w-6 h-6 text-red-600" />
            FAQ Manager
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Manage frequently asked questions
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchFaqs} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); window.scrollTo(0, 0); }} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-1" /> Add FAQ
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="dark:bg-gray-800 border-red-200 dark:border-red-800">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg dark:text-white">
              {editingFaq ? 'Edit FAQ' : 'Create New FAQ'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-white">
                  Question *
                </label>
                <Input
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  placeholder="Enter the question"
                  required
                  className="dark:bg-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-white">
                  Answer *
                </label>
                <Textarea
                  name="answer"
                  value={formData.answer}
                  onChange={handleChange}
                  placeholder="Enter the answer"
                  rows={4}
                  required
                  className="dark:bg-gray-700"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full p-2 border dark:border-gray-700 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                  />
                  <span className="text-sm dark:text-white">Published</span>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700">
                  <Save className="w-4 h-4 mr-1" />
                  {editingFaq ? 'Update' : 'Create'} FAQ
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* FAQs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : faqs.length === 0 ? (
        <Card className="dark:bg-gray-800">
          <CardContent className="py-12 text-center">
            <HelpCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">No FAQs yet</p>
            <Button onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-1" /> Create First FAQ
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <Card key={faq.id} className={`dark:bg-gray-800 ${!faq.is_published ? 'opacity-60' : ''}`}>
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="text-gray-400 font-medium text-sm mt-1">#{index + 1}</span>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-medium dark:text-white">{faq.question}</h3>
                        {!faq.is_published && (
                          <Badge variant="secondary" className="text-xs">
                            <EyeOff className="w-3 h-3 mr-1" /> Draft
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs capitalize">
                          {faq.category}
                        </Badge>
                      </div>
                      {expandedId !== faq.id && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {expandedId === faq.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {expandedId === faq.id && (
                  <div className="mt-4 pt-4 border-t dark:border-gray-700">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-4">
                      {faq.answer}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePublished(faq);
                        }}
                      >
                        {faq.is_published ? (
                          <><EyeOff className="w-4 h-4 mr-1" /> Unpublish</>
                        ) : (
                          <><Eye className="w-4 h-4 mr-1" /> Publish</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(faq);
                        }}
                      >
                        <Pencil className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(faq.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Stats */}
      {faqs.length > 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Total: {faqs.length} FAQs | Published: {faqs.filter(f => f.is_published).length} | Draft: {faqs.filter(f => !f.is_published).length}
        </div>
      )}
    </div>
  );
};

export default FAQManager;
